import { CodedProperty, NamedProperty } from "sprint-common-web";

import { AncillaryContentSubtype } from "../../../../../../common/types/AncillaryContentType";
import { BuzzContentReference } from "../../../../../../common/types/BuzzContentReference";
import { ContentReferenceType } from "../../../../../../common/types/ContentReferenceType";
import { ContentSource } from "../../../../../../common/types/ContentSource";
import { MovieContentSubtype } from "../../../../../../common/types/MovieContentSubtype";
import { TvSeriesContentSubtype } from "../../../../../../common/types/TvSeriesContentSubtype";

export type CloudFileAssociationContentDetails =
    | CloudFileAssociationMovieDetails
    | CloudFileAssociationTvSeriesDetails
    | CloudFileAssociationAncillaryDetails;

/* movie */
export interface CloudFileAssociationMovieDetails {
    type: ContentReferenceType.Movie;
    subtype: MovieContentSubtype;
    source: ContentSource;
    id: string;
    title: string;
    buzz?: { id: string; isArchived: boolean; isDeleted: boolean };
    versions: CloudFileAssociationMovieVersionDetails[];
}

export interface CloudFileAssociationMovieVersionDetails {
    id: string;
    buzzVersion?: {
        id: string;
        name: string;
        notes?: string;
        isArchived: boolean;
    };
    type: NamedProperty;
    brandings?: CodedProperty[];
    distributor: NamedProperty;
    mergedTargetId?: string;
}

export const ensureIsMovieDetails: (
    details: CloudFileAssociationContentDetails
) => asserts details is CloudFileAssociationMovieDetails = (
    details: CloudFileAssociationContentDetails
): asserts details is CloudFileAssociationMovieDetails => {
    if (details.type !== ContentReferenceType.Movie) throw Error("Content is not a Movie");
};

/* tv */
export interface CloudFileAssociationTvSeriesDetails {
    type: ContentReferenceType.TvSeries;
    subtype: TvSeriesContentSubtype;
    source: ContentSource;
    id: string;
    title: string;
    buzzId?: string;
    orphaned: boolean;
    seasons: CloudFileAssociationTvSeasonDetails[];
    mergedTargetId?: string;
}

export interface CloudFileAssociationTvSeasonDetails {
    id: string;
    name: string;
    number: number;
    buzzId?: string;
    episodes: CloudFileAssociationTvEpisodeDetails[];
    mergedTargetId?: string;
}

export interface CloudFileAssociationTvEpisodeDetails {
    id: string;
    title: string;
    number: number;
    buzz?: { id: string; isArchived: boolean; isDeleted: boolean };
    versions: CloudFileAssociationTvEpisodeVersionDetails[];
    mergedTargetId?: string;
}

export interface CloudFileAssociationTvEpisodeVersionDetails {
    id: string;
    buzzVersion?: {
        id: string;
        name: string;
        notes?: string;
        isArchived: boolean;
    };
    mergedTargetId?: string;
    type: NamedProperty;
    distributor: NamedProperty;
    brandings?: CodedProperty[];
}

export const ensureIsTvSeriesDetails: (
    details: CloudFileAssociationContentDetails
) => asserts details is CloudFileAssociationTvSeriesDetails = (
    details: CloudFileAssociationContentDetails
): asserts details is CloudFileAssociationTvSeriesDetails => {
    if (details.type !== ContentReferenceType.TvSeries) throw Error("Content is not a TvSeries");
};

/* ancillary */
export interface CloudFileAssociationAncillaryDetails {
    type: ContentReferenceType.Ancillary;
    subtype: AncillaryContentSubtype;
    source: ContentSource;
    id: string;
    title: string;
    buzz?: BuzzContentReference;
    version: CloudFileAssociationAncillaryVersionDetails;
    isMerged: boolean;
}

export interface CloudFileAssociationAncillaryVersionDetails {
    id: string;
    type: NamedProperty;
    airlines: CodedProperty[];
    distributor: NamedProperty;
    buzzId?: string;
}

export const ensureIsAncillaryDetails: (
    details: CloudFileAssociationContentDetails
) => asserts details is CloudFileAssociationAncillaryDetails = (
    details: CloudFileAssociationContentDetails
): asserts details is CloudFileAssociationAncillaryDetails => {
    if (details.type !== ContentReferenceType.Ancillary) throw Error("Content is not an Ancillary");
};
